import React from 'react';
import classNames from 'classnames';
import { Flex, Tag, Typography } from 'antd';
import { Button, Tooltip } from '@bettercomp/ui-components';
import { CheckCircleFilled, CheckCircleOutlined } from '@ant-design/icons';
import type { NotificationOut } from 'service/notificationsService';
import { formatRelativeDateTime } from 'utils/relativeDate';
import cn from './NotificationShell.module.scss';

type Props = {
  notification: NotificationOut;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  content: React.ReactNode;
};

export function NotificationShell({ notification, onMarkRead, onMarkUnread, content }: Props) {
  const isUnread = notification.read_at === null;
  // Resolved/expired rows are history: the backend rejects read-state changes
  // with 409, so the toggle is not rendered at all.
  const isLocked = notification.resolved || notification.expired;
  // Read rows and resolved/expired history rows share the muted "inactive" look.
  const isInactive = !isUnread || isLocked;
  const handleMarkRead = () => onMarkRead(notification.id);
  const handleMarkUnread = () => onMarkUnread(notification.id);

  return (
    <Flex
      align="flex-start"
      justify="space-between"
      gap={8}
      className={classNames(cn.shell, { [cn.inactive]: isInactive })}
      data-testid="notification-shell"
    >
      <Flex vertical gap={2} flex={1}>
        {content}
        <Typography.Text type="secondary" className={cn.timestamp}>
          {formatRelativeDateTime(notification.created_at)}
        </Typography.Text>
      </Flex>
      {isLocked && <Tag data-testid="notification-status-tag" className={cn.tag}>{notification.resolved ? 'Resolved' : 'Expired'}</Tag>}
      {!isLocked &&
        (isUnread ? (
          <Tooltip title="Mark as read">
            <Button
              shape="circle"
              variant="text"
              color="primary"
              classNames={{ icon: cn.icon }}
              icon={<CheckCircleOutlined />}
              size="small"
              onClick={handleMarkRead}
              data-testid="unread-indicator"
            />
          </Tooltip>
        ) : (
          <Tooltip title="Mark as unread">
            <Button
              shape="circle"
              variant="text"
              color="default"
              classNames={{ icon: cn.icon }}
              icon={<CheckCircleFilled />}
              size="small"
              onClick={handleMarkUnread}
              data-testid="read-indicator"
            />
          </Tooltip>
        ))}
    </Flex>
  );
}
